/**
 * Roster segmentation, shared by the teacher attendance page and the admin pickup
 * roster: school first, then grade.
 *
 * Teachers work the pickup line school by school — one school's van or walking group
 * arrives at a time — and within a school they call students by grade. Grouping the
 * roster the same way means the screen matches the order of the physical queue.
 *
 * School names are grouped EXACTLY as entered on the student record. Two spellings of
 * the same school therefore render as two sections; that is deliberate, so the roster
 * never quietly disagrees with the student data an admin can see and fix.
 */

import { roomGroupKey, roomSortKey } from '../types/roomNumber';

/** The subset of a roster entry this module needs. */
export interface GroupableStudent {
  /** Free-text school from `students.metadata->>'school'`. */
  school: string | null;
  /** Free-text grade from `students.metadata->>'grade'`. */
  grade: string | null;
  /** Digits-only room from `students.metadata->>'room_number'`, when set. */
  roomNumber?: string | null;
}

/** Heading used when a student record has no school on it. */
export const UNKNOWN_SCHOOL = 'No school listed';
/** Heading used when a student record has no grade on it. */
export const UNKNOWN_GRADE = 'No grade';

export interface RoomCluster<T> {
  /** Room number as entered, or null for students with none on file. */
  room: string | null;
  students: T[];
}

export interface GradeGroup<T> {
  grade: string;
  students: T[];
  /**
   * The same students, clustered by room number. A *soft* grouping: it never splits a
   * grade into separate sections, it only orders the one list and marks where each room
   * begins, so a grade still reads as a single block.
   */
  rooms: RoomCluster<T>[];
  /** True when at least one student in this grade has a room on file. */
  hasRooms: boolean;
}

export interface SchoolGroup<T> {
  school: string;
  grades: GradeGroup<T>[];
  total: number;
}

/** Trim and collapse whitespace; empty or missing becomes null. */
function clean(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim().replace(/\s+/g, ' ');
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The one canonical spelling of a grade: `TK`, `K`, or `1`…`12`. `null` when the label
 * means nothing we recognize.
 *
 * `students.metadata->>'grade'` is free text, and the same third-grader is written `3`,
 * `G3`, `Grade 3` or `3rd` depending on who typed it. `gradeRank` below already knows how
 * to read all of those, but it answers a sort question; this answers an identity one —
 * which is what deciding whether a child is in Chinese G3 needs.
 *
 * Mirrored exactly by `public.normalize_grade(text)` in the database, which derives
 * virtual-class membership. The two are tested against the same fixture (see
 * `rosterGrouping.test.ts` and the verification block in
 * `supabase/patches/20260814_virtual_class_attendance.sql`) precisely because a
 * disagreement between them would not fail anything loudly — it would quietly leave a
 * child off a roster they belong on.
 *
 * Returns `null` rather than guessing. A grade nobody can read is a data-quality problem
 * to surface, not one to paper over with a default.
 */
export function normalizeGrade(grade: string | null | undefined): string | null {
  const g = (grade ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
  if (g === '') return null;
  if (/^(INCOMING\s+)?TK$/.test(g)) return 'TK';
  if (/^(INCOMING\s+)?K$/.test(g)) return 'K';
  const numeric = /^(?:GRADE\s*|G)?(\d{1,2})(?:ST|ND|RD|TH)?$/.exec(g);
  // Number() then back to string so '03' and '3' cannot become two different grades.
  if (numeric) return String(Number(numeric[1]));
  return null;
}

/**
 * Sort weight for a grade label. BHCS grades run TK → K → 1…12, which is neither
 * alphabetical ("K" before "TK", "10" before "2") nor numeric, so the order is spelled
 * out here. Anything unrecognized sorts to the end, alphabetically among its peers.
 */
export function gradeRank(grade: string): number {
  const g = grade.trim().toUpperCase();
  if (g === 'TK') return 0;
  // Leftover from registration-time grades ("Incoming TK"); belongs beside TK, not in
  // the unrecognized bucket at the bottom.
  if (/^INCOMING\s+TK$/.test(g)) return 1;
  if (g === 'K' || /^INCOMING\s+K$/.test(g)) return 2;
  const numeric = /^(?:GRADE\s*|G)?(\d{1,2})(?:ST|ND|RD|TH)?$/.exec(g);
  if (numeric) return 10 + Number(numeric[1]);
  if (g === UNKNOWN_GRADE.toUpperCase()) return 9000;
  return 8000;
}

/**
 * Trailing words that identify a place as a school and carry no information once you are
 * already looking at a list of schools. Only stripped from the END of a name, so
 * "Episcopal Day School of St. Matthew" keeps its middle "School" and stays intact.
 */
const SCHOOL_SUFFIX_RE = /\s+(?:elementary\s+school|elementary|academy|school)$/i;

/**
 * A school name short enough to sit in a tab. "Foster City Elementary School" becomes
 * "Foster City" — nine tabs of full names is several screens of horizontal scrolling on
 * the phone this screen is used on.
 *
 * Never returns an empty string: a name that is nothing but a suffix ("School") is left
 * alone rather than shortened away.
 */
export function shortSchoolLabel(school: string): string {
  const shortened = school.replace(SCHOOL_SUFFIX_RE, '').trim();
  return shortened.length > 0 ? shortened : school;
}

/**
 * Short labels for a set of schools, keyed by full name.
 *
 * Shortening is abandoned for any pair that would collide — two different schools must
 * never render as the same tab. That is per-collision, not global: unrelated names keep
 * their short form.
 */
export function shortSchoolLabels(schools: readonly string[]): Map<string, string> {
  const byShort = new Map<string, string[]>();
  for (const school of schools) {
    const short = shortSchoolLabel(school);
    const bucket = byShort.get(short);
    if (bucket) bucket.push(school);
    else byShort.set(short, [school]);
  }

  const labels = new Map<string, string>();
  for (const [short, originals] of byShort) {
    for (const original of originals) {
      labels.set(original, originals.length === 1 ? short : original);
    }
  }
  return labels;
}

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th" … including the 11th/12th/13th exceptions. */
function ordinal(n: number): string {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/**
 * A grade as a teacher would say it out loud: "2" becomes "2nd Grade", "K" becomes
 * "Kindergarten". The stored value stays raw — only the heading is dressed up.
 *
 * Anything unrecognized is passed through untouched, so an unusual grade on a student
 * record still reads as itself rather than being mangled into a wrong label.
 */
export function formatGradeLabel(grade: string): string {
  const g = grade.trim().toUpperCase();
  if (g === 'TK') return 'TK';
  if (/^INCOMING\s+TK$/.test(g)) return 'Incoming TK';
  if (g === 'K') return 'Kindergarten';
  if (/^INCOMING\s+K$/.test(g)) return 'Incoming Kindergarten';
  const numeric = /^(?:GRADE\s*|G)?(\d{1,2})(?:ST|ND|RD|TH)?$/.exec(g);
  if (numeric) return `${ordinal(Number(numeric[1]))} Grade`;
  return grade;
}

/** Anything with a display name, which is all this comparator needs. */
export interface NameableStudent {
  /** "First Last" — how a name is displayed. */
  studentName: string;
}

/**
 * Roster order: by given name, then by the whole name to break a tie.
 *
 * Surname order is how a register is *printed*; a first name is how this screen is *used*.
 * A parent at the gate says "Emma", a teacher knows a six-year-old as Emma, and a substitute
 * scanning for the child in front of them is scanning for the name they were just given —
 * none of which is the letter the roster used to be ordered on.
 *
 * The second comparison matters more than it looks: two Emmas must fall in surname order
 * rather than in whatever order the RPC returned them, because they are exactly the pair a
 * teacher has to tell apart. Comparing the full "First Last" string alone would not do it —
 * where a space sorts against a letter is left to the locale, so "Emma Chen" and "Emmanuel
 * Ada" have no dependable order between them. Comparing the first names explicitly does.
 */
export function compareNamesByFirstName(a: string, b: string): number {
  const firstOf = (name: string) => name.split(' ')[0];
  return firstOf(a).localeCompare(firstOf(b)) || a.localeCompare(b);
}

/**
 * The same rule on a roster row that carries its name as `studentName`.
 *
 * The teacher roster and the admin pickup roster spell that field differently
 * (`studentName` / `displayName`), which is why the rule above is the one that is shared —
 * the two screens are the same dismissal line and must not read in two different orders.
 */
export function compareByFirstName<T extends NameableStudent>(a: T, b: T): number {
  return compareNamesByFirstName(a.studentName, b.studentName);
}

/**
 * Cluster one grade's students by room number, in room order, with the roomless last.
 *
 * Rooms group on their numeric value, so "013" and "13" — the same door written two ways —
 * land together. That is deliberately unlike school names, which group on exact spelling:
 * two similar school names may be two different schools, whereas room numbers are
 * integers and equality is unambiguous. The label shown is the spelling most students in
 * the cluster used.
 */
function clusterByRoom<T extends GroupableStudent>(students: T[]): RoomCluster<T>[] {
  const byKey = new Map<string, { spellings: Map<string, number>; students: T[] }>();
  const roomless: T[] = [];

  for (const student of students) {
    const room = clean(student.roomNumber);
    if (!room) {
      roomless.push(student);
      continue;
    }
    const key = roomGroupKey(room);
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = { spellings: new Map(), students: [] };
      byKey.set(key, bucket);
    }
    bucket.students.push(student);
    bucket.spellings.set(room, (bucket.spellings.get(room) ?? 0) + 1);
  }

  const clusters: RoomCluster<T>[] = Array.from(byKey.entries())
    .sort(([a], [b]) => roomSortKey(a) - roomSortKey(b))
    .map(([, bucket]) => {
      // Most-used spelling wins; ties break on the longer one, so a zero-padded "013"
      // beats "13" rather than flipping between renders.
      const label = Array.from(bucket.spellings.entries()).sort(
        ([aText, aCount], [bText, bCount]) => bCount - aCount || bText.length - aText.length || aText.localeCompare(bText),
      )[0][0];
      return { room: label, students: bucket.students };
    });

  if (roomless.length > 0) clusters.push({ room: null, students: roomless });
  return clusters;
}

/**
 * Group students by school, then by grade within each school.
 *
 * Schools sort alphabetically with the "no school" bucket last; grades sort by
 * `gradeRank`. Students keep the order they arrive in — the RPC already sorts them by
 * (last_name, first_name), so the result reads alphabetically inside each grade.
 */
export function groupBySchoolThenGrade<T extends GroupableStudent>(students: T[]): SchoolGroup<T>[] {
  const bySchool = new Map<string, Map<string, T[]>>();

  for (const student of students) {
    const school = clean(student.school) ?? UNKNOWN_SCHOOL;
    const grade = clean(student.grade) ?? UNKNOWN_GRADE;
    let grades = bySchool.get(school);
    if (!grades) {
      grades = new Map<string, T[]>();
      bySchool.set(school, grades);
    }
    const bucket = grades.get(grade);
    if (bucket) bucket.push(student);
    else grades.set(grade, [student]);
  }

  return Array.from(bySchool.entries())
    .sort(([a], [b]) => {
      // Students with no school on file go last, so real schools stay together at the top.
      if (a === UNKNOWN_SCHOOL) return b === UNKNOWN_SCHOOL ? 0 : 1;
      if (b === UNKNOWN_SCHOOL) return -1;
      return a.localeCompare(b);
    })
    .map(([school, grades]) => {
      const gradeGroups = Array.from(grades.entries())
        .sort(([a], [b]) => gradeRank(a) - gradeRank(b) || a.localeCompare(b))
        .map(([grade, students]) => {
          const rooms = clusterByRoom(students);
          return {
            grade,
            // Flattened in room order, so the list and the clusters always agree.
            students: rooms.flatMap(r => r.students),
            rooms,
            hasRooms: rooms.some(r => r.room !== null),
          };
        });
      return {
        school,
        grades: gradeGroups,
        total: gradeGroups.reduce((sum, g) => sum + g.students.length, 0),
      };
    });
}

// ── Grade bands ───────────────────────────────────────────────────────────────
//
// The little ones and the big ones dismiss separately — different times, different
// doors, different staff — so a pickup run is a (school, band) pair, not a school.
// The band is derived from the STUDENT'S GRADE rather than from the program they are
// enrolled in: the two big after-school programs happen to be split TK&K / G1-G5, but
// the Flexible and Mini TK programs cross that line, so program would misfile them.

export type GradeBand = 'tk_k' | 'g1_5' | 'other';

/** Band headings, in the words used on the pickup board. */
export const GRADE_BAND_LABEL: Record<GradeBand, string> = {
  tk_k: 'TK & K',
  g1_5: 'G1 – G5',
  other: 'Other grades',
};

/** Bands in dismissal order. `other` is last — it is an exception, not a cohort. */
export const GRADE_BAND_ORDER: readonly GradeBand[] = ['tk_k', 'g1_5', 'other'];

/**
 * Which band a grade dismisses with.
 *
 * Grades outside TK–5 land in `other` rather than being folded into `g1_5`. A 6th
 * grader on an after-school roster is a data question worth seeing, and quietly filing
 * them with the 5th graders would hide it. `other` sections simply do not render when
 * empty, so the normal case costs nothing.
 */
export function gradeBand(grade: string): GradeBand {
  const rank = gradeRank(grade);
  // gradeRank: TK=0, Incoming TK=1, K=2, grade N=10+N, unrecognized=8000, unknown=9000.
  if (rank <= 2) return 'tk_k';
  if (rank >= 11 && rank <= 15) return 'g1_5';
  return 'other';
}

/**
 * One pickup run: everyone a staff member collects from one school in one band.
 *
 * Carries the same `grades` shape as `SchoolGroup`, so a run renders with the existing
 * grade/room logic untouched.
 */
export interface RosterRun<T> {
  /** Stable identity for React keys and for filtering. */
  key: string;
  /** School name exactly as entered on the student records. */
  school: string;
  /** Shortened for headings; falls back to the full name when shortening would collide. */
  shortSchool: string;
  band: GradeBand;
  bandLabel: string;
  grades: GradeGroup<T>[];
  total: number;
  /** Room numbers present in this run, in room order — a header hint for the pickup line. */
  rooms: string[];
}

/** The subset of a student this module needs to place them in a run. */
export type RunGroupableStudent = GroupableStudent;

/**
 * Split students into pickup runs: school first (alphabetical, "no school" last), then
 * band in dismissal order, then the usual grade/room grouping within each.
 *
 * School-major ordering keeps a school's two runs adjacent, so the board reads as
 * "Beach Park TK&K, Beach Park G1-G5, Audubon TK&K, …" rather than interleaving schools.
 * Empty (school, band) pairs are omitted entirely — the grid is sparse and blank cards
 * would be most of it.
 */
export function groupIntoRuns<T extends RunGroupableStudent>(students: T[]): RosterRun<T>[] {
  const byBand = new Map<GradeBand, T[]>();
  for (const student of students) {
    const band = gradeBand(clean(student.grade) ?? UNKNOWN_GRADE);
    const bucket = byBand.get(band);
    if (bucket) bucket.push(student);
    else byBand.set(band, [student]);
  }

  // Group each band independently, then re-key by school so the two runs of one school
  // can be emitted together.
  const bandGroups = new Map<GradeBand, Map<string, SchoolGroup<T>>>();
  const schoolOrder: string[] = [];
  for (const band of GRADE_BAND_ORDER) {
    const inBand = byBand.get(band);
    if (!inBand) continue;
    const groups = new Map<string, SchoolGroup<T>>();
    for (const group of groupBySchoolThenGrade(inBand)) {
      groups.set(group.school, group);
      if (!schoolOrder.includes(group.school)) schoolOrder.push(group.school);
    }
    bandGroups.set(band, groups);
  }

  // One alphabetical pass over the union of schools, so band membership cannot change
  // the order schools appear in.
  schoolOrder.sort((a, b) => {
    if (a === UNKNOWN_SCHOOL) return b === UNKNOWN_SCHOOL ? 0 : 1;
    if (b === UNKNOWN_SCHOOL) return -1;
    return a.localeCompare(b);
  });
  const shortLabels = shortSchoolLabels(schoolOrder);

  const runs: RosterRun<T>[] = [];
  for (const school of schoolOrder) {
    for (const band of GRADE_BAND_ORDER) {
      const group = bandGroups.get(band)?.get(school);
      if (!group || group.total === 0) continue;
      const rooms: string[] = [];
      for (const grade of group.grades) {
        for (const cluster of grade.rooms) {
          if (cluster.room !== null && !rooms.includes(cluster.room)) rooms.push(cluster.room);
        }
      }
      rooms.sort((a, b) => roomSortKey(a) - roomSortKey(b));
      runs.push({
        key: `${school}::${band}`,
        school,
        shortSchool: shortLabels.get(school) ?? school,
        band,
        bandLabel: GRADE_BAND_LABEL[band],
        grades: group.grades,
        total: group.total,
        rooms,
      });
    }
  }
  return runs;
}
